import {
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { updateClientParameter } from '../../../sic-lib/api/config/config';
import { useAiPromptsMetadata } from '../../../sic-lib/api/hooks/useAiPrompts';
import { AiPromptFeatures } from '../../../sic-lib/types/Ai';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { requestConfig } from '../../../store/users';

interface Props {
    promptId?: number;
}

export default function AiSettings({ promptId }: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const { data: prompts, isLoading } = useAiPromptsMetadata();

    const chatReplyPromptId = useAppSelector(state => state.user.chatReplyPromptId);
    const transcriptPromptId = useAppSelector(state => state.user.transcriptPromptId);
    const transcriptSummaryPromptId = useAppSelector(state => state.user.transcriptSummaryPromptId);

    const groupedPrompts = React.useMemo(() => {
        if (!prompts)
            return {
                transcription: [],
                transcriptSummary: [],
                chatReply: [],
            };
        return {
            transcription: prompts.filter(p => p.feature === AiPromptFeatures.Transcript),
            transcriptSummary: prompts.filter(
                p => p.feature === AiPromptFeatures.TranscriptSummary,
            ),
            chatReply: prompts.filter(p => p.feature === AiPromptFeatures.ChatReply),
        };
    }, [prompts]);

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        await updateClientParameter({
            name: name,
            value: value.toString(),
        });
        dispatch(requestConfig());
    };

    if (isLoading)
        return (
            <Grid display="flex" justifyContent="center" m={2}>
                <CircularProgress />
            </Grid>
        );

    return (
        <>
            <Card>
                <CardHeader title="Smart Chat Reply" />
                <Divider />
                <CardContent>
                    <FormControl>
                        <RadioGroup
                            value={chatReplyPromptId}
                            onChange={handleChange}
                            name="ChatReplyPromptId"
                        >
                            {groupedPrompts.chatReply.map(p => (
                                <FormControlLabel
                                    key={p.promptId}
                                    value={p.promptId}
                                    control={<Radio />}
                                    label={p.title}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </CardContent>
            </Card>
            <Card>
                <CardHeader title="Call Transcription" />
                <Divider />
                <CardContent>
                    <FormControl>
                        <RadioGroup
                            value={transcriptPromptId}
                            onChange={handleChange}
                            name="TranscriptPromptId"
                        >
                            {groupedPrompts.transcription.map(p => (
                                <FormControlLabel
                                    key={p.promptId}
                                    value={p.promptId}
                                    control={<Radio />}
                                    label={p.title}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </CardContent>
            </Card>
            <Card>
                <CardHeader title="Call Transcription Summary" />
                <Divider />
                <CardContent>
                    <FormControl>
                        <RadioGroup
                            value={transcriptSummaryPromptId}
                            onChange={handleChange}
                            name="TranscriptSummaryPromptId"
                        >
                            {groupedPrompts.transcriptSummary.map(p => (
                                <FormControlLabel
                                    key={p.promptId}
                                    value={p.promptId}
                                    control={<Radio />}
                                    label={p.title}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </CardContent>
            </Card>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({}));
